import React, { useEffect, useMemo, useState, memo, useImperativeHandle } from 'react';
import { View, StyleSheet } from 'react-native';
import Spacer from 'design-system/Spacer';
import ShakeContainer from 'design-system/ShakeContainer';
import PinPad from 'design-system/MoneyInputPinPad';
import QuantityInputField from './QuantityInputField';
import { roundQuantity, formatNumberOfShares } from '../utils';
const captions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '<'];
const LIMIT = +Infinity; // the limit is the number of shares user has to sell, so there is no point in this global limit
const MANTISSA = 2;
const QuantityInput = React.forwardRef(({ onValueChange, registerShakeCallback, setValueRef, colors, max, flex, initialValue, header, onExceedMax, numPadHeader, inputFieldStyle, pinPadStyle, maximumFractionDigits, }, ref) => {
    const [value, setValue] = useState(initialValue ?? 0);
    const [fractionDigits, setFractionDigits] = useState(0);
    const shakeRef = React.useRef(undefined);
    const maxRef = React.useRef(max === undefined ? LIMIT : roundQuantity(max));
    useImperativeHandle(ref, () => ({
        clear: () => {
            shakeRef.current?.();
            setValue(0);
            setFractionDigits(0);
        },
        shake: () => {
            shakeRef.current?.();
        },
    }));
    useEffect(() => {
        if (max !== undefined) {
            maxRef.current = roundQuantity(max, maximumFractionDigits);
        }
    }, [max, maximumFractionDigits]);
    useEffect(() => {
        if (setValueRef) {
            // eslint-disable-next-line no-param-reassign
            setValueRef.current = (value) => {
                const formatted = roundQuantity(value, maximumFractionDigits).toString();
                const match = formatted.match(/\.(\d+)$/);
                if (match) {
                    setFractionDigits(match[1].length + 1);
                }
                else {
                    setFractionDigits(0);
                }
                setValue(value);
            };
        }
    }, [maximumFractionDigits, setValueRef]);
    const displayValue = useMemo(() => {
        if (fractionDigits === 0)
            return formatNumberOfShares(value, false, false, 0);
        const offset = fractionDigits > 1 ? 1 : 0;
        const formatted = formatNumberOfShares(value, false, false, Math.min(fractionDigits - offset, maximumFractionDigits ?? MANTISSA), maximumFractionDigits);
        if (fractionDigits === 1)
            return formatted.substring(0, formatted.length - 1);
        return formatted;
    }, [value, fractionDigits, maximumFractionDigits]);
    const { input, pinpad } = colors ?? {};
    useEffect(() => {
        onValueChange?.(value);
    }, [value, onValueChange]);
    const onInput = (text) => {
        switch (text) {
            case '<':
                switch (fractionDigits) {
                    case 0:
                        if (value === 0)
                            shakeRef.current?.();
                        else
                            setValue(Math.floor(value / 10));
                        break;
                    case 1:
                        setFractionDigits(fractionDigits - 1);
                        break;
                    default: {
                        const shift = 10 ** (fractionDigits - 2);
                        const newValue = Math.floor(value * shift) / shift;
                        setFractionDigits(fractionDigits - 1);
                        setValue(newValue);
                    }
                }
                break;
            case '.':
                if (fractionDigits === 0 && maximumFractionDigits !== 0) {
                    setFractionDigits(1);
                }
                else {
                    shakeRef.current?.();
                }
                break;
            default: {
                let newValue = value;
                if (fractionDigits === 0) {
                    newValue = value * 10 + Number.parseInt(text, 10);
                }
                else if (fractionDigits <= (maximumFractionDigits ?? MANTISSA)) {
                    const shift = 10 ** fractionDigits;
                    newValue = value + Number.parseInt(text, 10) / shift;
                }
                newValue = Number(newValue.toFixed(fractionDigits));
                if ((newValue === value && fractionDigits >= (maximumFractionDigits ?? MANTISSA) + 1) ||
                    newValue < 0 ||
                    newValue > maxRef.current) {
                    onExceedMax?.();
                    shakeRef.current?.();
                }
                else {
                    if (fractionDigits > 0) {
                        setFractionDigits(fractionDigits + 1);
                    }
                    setValue(newValue);
                }
            }
        }
    };
    const registerCallback = (callback) => {
        shakeRef.current = callback;
        registerShakeCallback?.(callback);
    };
    return (<View style={[styles.container, flex && styles.flex]}>
        {header || (flex && <Spacer flex/>)}
        <ShakeContainer registerCallback={registerCallback}>
          <QuantityInputField style={inputFieldStyle} color={input} value={displayValue}/>
        </ShakeContainer>
        {numPadHeader || (flex && <Spacer flex/>)}
        <PinPad style={[pinPadStyle]} captions={captions} onInput={onInput} color={pinpad}/>
      </View>);
});
const styles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
});
export default memo(QuantityInput);
